import {
  CALCULATION_DEBOUNCE_QUEUE_ADD,
  CALCULATION_DEBOUNCE_QUEUE_REMOVE,
  CALCULATION_PROCESS_CLEAR_ALL,
  CALCULATION_PROCESS_END,
  CALCULATION_PROCESS_START,
  CALCULATION_PROCESS_STATUS_UPDATE,
  SET_AUTO_CALC_DISABLED,
} from 'actions/designer'
import omit from 'lodash/omit'
import { Reducer } from 'redux'
import appStorage from 'storage/appStorage'
import { RootState } from 'types/state'

export type StatusType =
  | 'preparingClustersRequest'
  | 'preparingCalculationRequest'
  | 'waitingCalculation'
  | 'waitingCluster'
  | 'handlingClusterSuccess'
  | 'handlingCalculationSuccess'
  | 'initialCalculationComplete'
  | 'error'

export type Status = {
  uuid: string
  requireClusters: boolean
  status: StatusType
}
export interface SystemCalculationsQueuedState {
  debounceQueue: string[]
  processQueue: { [key: string]: Status }
  autoCalculateDisabled: boolean | undefined
}

const getStatus = (uuid: string, requireClusters: boolean, forceShadingCalculation?: boolean) => {
  return {
    uuid,
    requireClusters,
    status: requireClusters ? 'preparingClustersRequest' : 'preparingCalculationRequest',
    forceShadingCalculation: forceShadingCalculation || undefined,
  }
}

export const AUTOCALC_DISABLED_KEY = 'autoCalculateDisabled'

const defaultState: SystemCalculationsQueuedState = {
  debounceQueue: [],
  processQueue: {},
  autoCalculateDisabled: undefined,
}

export const systemCalculationsQueuedReducer: Reducer<SystemCalculationsQueuedState> = (
  previousState = defaultState,
  { type, payload }
) => {
  const { uuid, requireClusters, status, forceShadingCalculation } = payload || {}

  if (type === CALCULATION_DEBOUNCE_QUEUE_ADD) {
    const newDebounceQueue = [...previousState.debounceQueue]
    if (!newDebounceQueue.includes(uuid)) {
      newDebounceQueue.push(uuid)
    }
    return {
      ...previousState,
      debounceQueue: newDebounceQueue,
    }
  } else if (type === CALCULATION_DEBOUNCE_QUEUE_REMOVE) {
    const newDebounceQueue = [...previousState.debounceQueue]
    const indexInDebounceQueue = newDebounceQueue.indexOf(uuid)
    newDebounceQueue.splice(indexInDebounceQueue, 1)
    return {
      ...previousState,
      debounceQueue: newDebounceQueue,
    }
  } else if (type === CALCULATION_PROCESS_START) {
    const newDebounceQueue = [...previousState.debounceQueue]
    const indexInDebounceQueue = newDebounceQueue.indexOf(uuid)
    if (indexInDebounceQueue === -1) {
      return previousState
    }

    newDebounceQueue.splice(indexInDebounceQueue, 1)

    return {
      ...previousState,
      processQueue: {
        ...previousState.processQueue,
        [uuid]: getStatus(uuid, requireClusters, forceShadingCalculation),
      },
      debounceQueue: newDebounceQueue,
    }
  } else if (type === CALCULATION_PROCESS_STATUS_UPDATE) {
    if (!previousState || !uuid) {
      return previousState || defaultState
    }

    let updated = false
    let newState = { ...previousState }

    if (previousState.processQueue[uuid]) {
      newState = {
        ...newState,
        processQueue: {
          ...previousState.processQueue,
          [uuid]: {
            ...previousState.processQueue[uuid],
            status,
          },
        },
      }
      updated = true
    }

    return updated ? newState : previousState
  } else if (type === CALCULATION_PROCESS_END) {
    return {
      ...previousState,
      processQueue: omit(previousState.processQueue, [uuid]),
    }
  } else if (type === CALCULATION_PROCESS_CLEAR_ALL) {
    return defaultState
  } else if (type === SET_AUTO_CALC_DISABLED) {
    const newState = {
      ...previousState,
      autoCalculateDisabled: payload.value,
    }
    appStorage.setBool(AUTOCALC_DISABLED_KEY, payload.value)
    return newState
  } else {
    return previousState
  }
}

export const systemCalculationsSelectors = {
  areAnyQueuedOrProcessing(state: RootState) {
    return (
      state.designer.systemCalculation.debounceQueue.length > 0 ||
      Object.keys(state.designer.systemCalculation.processQueue).length > 0
    )
  },
  isSystemQueued(state: RootState, uuid: string | undefined) {
    return !!uuid && state.designer.systemCalculation.debounceQueue.includes(uuid)
  },
  isSystemProcessing(state: RootState, uuid: string | undefined) {
    return !!uuid && !!state.designer.systemCalculation.processQueue[uuid]
  },
  getAutoCalcDisabled(state: RootState) {
    // Can't grab this at boot time due to quirky localStorage/sessionStorage behaviour
    if (state.designer.systemCalculation.autoCalculateDisabled === undefined) {
      return appStorage.getBool(AUTOCALC_DISABLED_KEY) || false
    }
    return state.designer.systemCalculation.autoCalculateDisabled
  },
}
